import { useMutation } from '@tanstack/react-query'
import { FileProcessingStatus } from '@/lib/api/company/fileProcessing/status'
import { uploadFiles } from '@/lib/api/company/fileProcessing/upload/uploadFiles'
import { getFileExtension } from '@/modules/imports/utils/getFileExtension'
import { zipFiles } from './zipFiles'

export type UploadMutationPayload =
  | File[]
  | { files: File[]; signal?: AbortSignal }

function normalizePayload(
  payload: UploadMutationPayload
): { files: File[]; signal?: AbortSignal } {
  return Array.isArray(payload)
    ? { files: payload, signal: undefined }
    : payload
}

/** Use to detect user-initiated abort so the UI can hide the error. */
export function isUploadAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (typeof err !== 'object' || err === null) return false
  const o = err as { code?: string; name?: string; message?: string }
  if (o.code === 'ERR_CANCELED' || o.name === 'CanceledError') return true
  const msg = (o.message ?? '').toLowerCase()
  if (msg.includes('abort') || msg.includes('cancel')) return true
  return false
}

const UPLOAD_ABORTED = new DOMException('Upload aborted', 'AbortError')

type Props = {
  companyId: string
  onSuccess: (fileId: string, taskId: string) => void
  setUploadedFilesCount?: (num: number) => void
}

export const useUploadMutation = ({
  companyId,
  onSuccess,
  setUploadedFilesCount,
}: Props) => {
  return useMutation<FileProcessingStatus[], unknown, UploadMutationPayload>({
    mutationFn: async (payload) => {
      const { files, signal } = normalizePayload(payload)
      if (!files.length) return []

      const taskId = crypto.randomUUID()

      // Zip all files client-side (STORE, no compression) → single HTTP round trip
      const zippedData = await zipFiles(files)

      if (signal?.aborted) throw UPLOAD_ABORTED

      const zipFile = new File([zippedData], 'upload.zip', { type: 'application/zip' })

      const formData = new FormData()
      formData.append('body', zipFile)
      formData.append('options', JSON.stringify({ isMinimalWages: false, filterOutEdgeDates: false }))

      const extensions = [...new Set(files.map((f) => getFileExtension(f)).filter(Boolean))]

      let res
      try {
        res = await uploadFiles({
          companyId,
          files: formData,
          lastChunk: true,
          count: files.length,
          taskId,
          extensions: extensions.length ? extensions : ['ddd'],
          signal,
        })
      } catch (err) {
        if (signal?.aborted || isUploadAbortError(err)) throw UPLOAD_ABORTED
        throw err
      }

      if (signal?.aborted) throw UPLOAD_ABORTED
      if (res.status !== 200) throw new Error('Upload failed')

      if (setUploadedFilesCount) {
        setUploadedFilesCount(files.length)
      }

      return [res.data]
    },

    onSuccess: async (results) => {
      if (!results.length) return
      const last = results[results.length - 1]
      const { id, taskId } = last
      onSuccess(id.toString(), taskId)
    },
  })
}
